import {
  Modal,
  Box,
  Container,
  Typography,
  IconButton,
  Link,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  ArrowForwardIosIcon,
  CloseIcon,
  InfoIcon,
  InfoOutlinedIcon,
} from "../icons";
import manualData from "../datas/manualContents.json";

const ManualModalStyle = {
  position: "relative",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  height: "90%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "var(--secondary-bg-color)",
  boxShadow: 24,
  borderRadius: 1,
  //   userSelect: "none",
};

type MenuItem = {
  mainMenu: string;
  subMenus?: string[];
};

export default function ManualModal({
  openManual,
  setOpenManual,
}: {
  openManual: boolean;
  setOpenManual: (open: boolean) => void;
}) {
  const menuItems = [
    {
      mainMenu: "홈",
      subMenus: [],
    },
    {
      mainMenu: "프로젝트 생성",
      subMenus: ["분류", "분석"],
    },
    {
      mainMenu: "메인 프로젝트 화면",
      subMenus: ["분류 페이지", "분석 페이지", "프로젝트 설정"],
    },
    {
      mainMenu: "학습",
      subMenus: ["[공통] 학습 실행", "학습 히스토리 확인"],
    },
    {
      mainMenu: "리포트",
      subMenus: ["생성", "설정"],
    },
  ];

  const [selectedMenu, setSelectedMenu] = useState<string>(
    menuItems[0].mainMenu
  );

  return (
    <Modal
      open={openManual}
      onClose={() => {
        setOpenManual(false);
      }}
    >
      <Box sx={ManualModalStyle}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            alignItems: "center",
            borderBottom: "1px solid var(--m-border-color)",
          }}
        >
          <Typography
            variant="h5"
            color="var(--m-header-text-color)"
            sx={{ fontWeight: "700" }}
          >
            데이터 분류 분석 솔루션 Manual
          </Typography>
          <IconButton
            onClick={() => {
              setOpenManual(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", height: "90%", width: "100%" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <ManualSidebar
              setOpenManual={setOpenManual}
              menuItems={menuItems}
              selectedMenu={selectedMenu}
              setSelectedMenu={setSelectedMenu}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 1,
              flex: 1,
            }}
          >
            <ManualContent
              selectedMenu={selectedMenu}
              setSelectedMenu={setSelectedMenu}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

const ManualSidebar = ({
  setOpenManual,
  menuItems,
  selectedMenu,
  setSelectedMenu,
}: {
  setOpenManual: (open: boolean) => void;
  menuItems: MenuItem[];
  selectedMenu: string;
  setSelectedMenu: (menu: string) => void;
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "180px",
        height: "100%",
        backgroundColor: "var(--m-sidebar-bg-color)",
        p: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{ borderBottom: "1px solid var(--dk-border, #ccc)", my: "5px" }}
      >
        Contents
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
        {menuItems.map((item) => {
          const selectedMainMenu = selectedMenu.split(" > ")[0];
          const isMainMenuSelected = selectedMainMenu === item.mainMenu;
          const isSubMenuSelected = item.subMenus?.some(
            (sub) => selectedMenu === `${item.mainMenu} > ${sub}`
          );

          return (
            <Box key={item.mainMenu}>
              <Typography
                variant="body1"
                onClick={() => setSelectedMenu(item.mainMenu)}
                fontWeight={isMainMenuSelected ? "bold" : "normal"}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  color: isMainMenuSelected
                    ? "var(--m-selected-menu-color)"
                    : "var(--m-menu-color)",
                }}
              >
                {item.mainMenu}
                <ArrowForwardIosIcon
                  sx={{
                    fontSize: "15px",
                    color: isMainMenuSelected
                      ? "var(--m-selected-menu-color)"
                      : "var(--m-menu-color)",
                  }}
                />
              </Typography>
              {item.subMenus &&
                item.subMenus?.length > 0 &&
                (isMainMenuSelected || isSubMenuSelected) && (
                  <Box sx={{ ml: 2, mt: 1 }}>
                    {item.subMenus.map((sub) => {
                      const isThisSubMenuSelected =
                        selectedMenu === `${item.mainMenu} > ${sub}`;
                      return (
                        <Typography
                          key={sub}
                          variant="body2"
                          sx={{
                            cursor: "pointer",
                            mb: 0.5,
                            color: isThisSubMenuSelected
                              ? "var(--m-selected-menu-color)"
                              : "var(--m-menu-color)",
                          }}
                          onClick={() =>
                            setSelectedMenu(`${item.mainMenu} > ${sub}`)
                          }
                        >
                          {sub}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const ManualContent = ({
  selectedMenu,
  setSelectedMenu,
}: {
  selectedMenu: string;
  setSelectedMenu: (menu: string) => void;
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const section = (manualData as any)[selectedMenu];

  const isMainMenuPage =
    section &&
    typeof section === "object" &&
    !Array.isArray(section) &&
    section.type === "main";

  const isArraySection = Array.isArray(section); // 예전 방식 유지할지 여부 결정

  const pageData = isArraySection
    ? section.find((p: any) => p.page === currentPage)
    : null;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);

    const timeout = setTimeout(() => {
      setLoading(false);
    }, 10);

    return () => clearTimeout(timeout);
  }, [selectedMenu]);

  return (
    <Box sx={{ width: "100%", overflowY: "auto", height: "100%" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            p: 1,
            // height: "100%",
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>매뉴얼을 로딩중입니다...</Typography>
        </Box>
      ) : isMainMenuPage ? (
        <>
          <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
              [{selectedMenu}]
            </Typography>
            {section.intro.map((text: string, idx: number) => (
              <Typography key={idx} variant="body1" sx={{ mb: 1 }}>
                {text}
              </Typography>
            ))}

            <Box sx={{ mt: 3 }}>
              {section.links.map((link: any, idx: number) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 2,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                      color: "#1976d2",
                    },
                  }}
                  onClick={() => setSelectedMenu(link.target)}
                >
                  <Typography variant="h6">{link.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {link.summary}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
            [{selectedMenu}]
          </Typography>
          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                paddingBottom: "70px",
              }}
            >
              {pageData ? (
                pageData.contents.map((item: any, index: number) => (
                  <Box key={index} id={`section-${index}`} sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: "700" }}>
                      {index + 1}. {item.title || ""}
                    </Typography>
                    {item.description.map((desc: string, i: number) => (
                      <Typography key={i} variant="body1" sx={{ mb: 0.5 }}>
                        {`${desc}`}
                      </Typography>
                    ))}
                    {Array.isArray(item.img) ? (
                      item.img.map((src: string, i: number) => (
                        <img
                          key={i}
                          src={src}
                          alt={`image-${index}-${i}`}
                          style={{
                            maxWidth: "100%",
                            borderRadius: 8,
                            width: "80%",
                            // height: "300px",
                            marginBottom: 8,
                          }}
                        />
                      ))
                    ) : (
                      <img
                        src={item.img}
                        alt={`image-${index}`}
                        style={{
                          maxWidth: "100%",
                          borderRadius: 8,
                          width: "80%",
                        }}
                      />
                    )}
                    {item.descriptionBottom &&
                      item.descriptionBottom.map((desc: string, i: number) => (
                        <Typography
                          key={i}
                          variant="body1"
                          sx={{ my: 0.5, ml: 3 }}
                        >
                          {`${desc}`}
                        </Typography>
                      ))}
                    <Box
                      sx={{
                        width: "100%",
                        borderBottom: "1px solid var(--dk-border, #ddd)",
                        mt: 1,
                      }}
                    />
                  </Box>
                ))
              ) : (
                <Box>
                  <Typography color="text.secondary">
                    해당 페이지에 내용이 없습니다.
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                width: 180,
                pl: 1,
                borderLeft: "1px solid var(--dk-border, #ddd)",
                borderBottom: "1px solid var(--dk-border, #ddd)",
                borderTop: "1px solid var(--dk-border, #ddd)",
                overflowY: "auto",
                position: "sticky",
                top: 0,
                backgroundColor: "var(--m-bg-color)",
                maxHeight: "100vh",
                alignSelf: "flex-start",
              }}
            >
              <Typography
                gutterBottom
                sx={{ fontSize: "18px", fontWeight: "bold", pt: 1, pl: 1 }}
              >
                목차
              </Typography>
              <List dense>
                {pageData?.contents.map((item: any, index: number) => (
                  <ListItemButton
                    key={index}
                    onClick={() => {
                      const el = document.getElementById(`section-${index}`);
                      if (el) {
                        el.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                  >
                    <ListItemText primary={`${index + 1}. ${item.title}`} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Box>
          {/* {pageData && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 3,
                position: "sticky",
                bottom: 0,
                width: "100%",
                backgroundColor: "var(--m-bg-color)",
                alignItems: "center",
                p: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={handlePrev}
                disabled={currentPage === 1}
              >
                이전
              </Button>
              <Typography variant="body2">
                Page {currentPage} / {section.length}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleNext}
                disabled={currentPage === section.length}
              >
                다음
              </Button>
            </Box>
          )} */}
        </Box>
      )}
    </Box>
  );
};
